import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import logo from '../Assets/blackLogo.png';
import './Navbar.css';

/* Center navigation. Mapped to real destinations:
   Ministries -> homepage programs section, Sermons -> YouTube channel. */
const NAV_LINKS = [
  { label: 'Home', to: '/', end: true, kind: 'route' },
  { label: 'About', to: '/about-us', kind: 'route' },
  { label: 'Ministries', to: '/#programs', kind: 'hash' },
  { label: 'Sermons', href: 'https://www.youtube.com/@thecitychurch-luzira2585', kind: 'external' },
  { label: 'News & Events', to: '/news-and-events', kind: 'route' },
  { label: 'Money Matters', to: '/financial', kind: 'route' },
  { label: 'Contact', to: '/contact-us', kind: 'route' },
];

function NavItem({ link, onClick }) {
  if (link.kind === 'external') {
    return (
      <a className="tcc-nav__link" href={link.href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {link.label}
      </a>
    );
  }
  if (link.kind === 'hash') {
    return (
      <Link className="tcc-nav__link" to={link.to} onClick={onClick}>
        {link.label}
      </Link>
    );
  }
  return (
    <NavLink
      to={link.to}
      end={link.end}
      className={({ isActive }) => `tcc-nav__link${isActive ? ' is-active' : ''}`}
      onClick={onClick}
    >
      {link.label}
    </NavLink>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY >= 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, closeMenu]);

  return (
    <nav className={`tcc-nav${scrolled ? ' tcc-nav--scrolled' : ''}`} aria-label="Primary">
      <div className="tcc-nav__inner">
        {/* Left: logo + brand */}
        <Link to="/" className="tcc-nav__logo" onClick={closeMenu} aria-label="The City Church, Home">
          <img src={logo} alt="The City Church" className="tcc-nav__logo-img" />
        </Link>

        {/* Center: navigation links (desktop) */}
        <ul className="tcc-nav__links">
          {NAV_LINKS.map((l) => (
            <li key={l.label} className="tcc-nav__item">
              <NavItem link={l} />
            </li>
          ))}
        </ul>

        {/* Right: CTA + mobile toggle */}
        <div className="tcc-nav__actions">
          <Link to="/give" className="tcc-btn tcc-btn--accent tcc-nav__give">Give</Link>
          <button
            type="button"
            className="tcc-nav__toggle"
            aria-expanded={open}
            aria-controls="tcc-nav-drawer"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`tcc-nav__backdrop${open ? ' is-open' : ''}`} onClick={closeMenu} aria-hidden="true" />
      <div id="tcc-nav-drawer" className={`tcc-nav__drawer${open ? ' is-open' : ''}`}>
        <ul className="tcc-nav__drawer-links">
          {NAV_LINKS.map((l) => (
            <li key={l.label}>
              <NavItem link={l} onClick={closeMenu} />
            </li>
          ))}
        </ul>
        <div className="tcc-nav__drawer-cta">
          <Link to="/join-mc" className="tcc-btn tcc-btn--ghost tcc-btn--block" onClick={closeMenu}>Plan a Visit</Link>
          <Link to="/give" className="tcc-btn tcc-btn--accent tcc-btn--block" onClick={closeMenu}>Give</Link>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
